import type { ShowPeriod, ShowPeriodTiming } from './showPeriods';

const DAY = 86400000;
const formatter = (timezone: string) => new Intl.DateTimeFormat('en-CA', {
  timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

export function formatShowWallTime(instant: string, timezone: string): string {
  const date = new Date(instant);
  if (!Number.isFinite(date.valueOf())) throw new Error('Enter a valid date and time.');
  const parts = Object.fromEntries(formatter(timezone).formatToParts(date).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

function wallMilliseconds(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) throw new Error('Enter a complete date and time.');
  const canonical = value.length === 16 ? `${value}:00` : value;
  const parsed = Date.parse(`${canonical}Z`);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString().slice(0, 19) !== canonical) {
    throw new Error('Enter a valid date and time.');
  }
  return parsed;
}

/** Return all matching instants, including both occurrences in a DST overlap. */
export function showWallTimeCandidates(value: string, timezone: string): string[] {
  const wall = wallMilliseconds(value);
  const canonical = new Date(wall).toISOString().slice(0, 19);
  const offsets = new Set<number>();
  // Sample on both sides of a possible transition, including fractional-hour zones.
  for (let hours = -48; hours <= 48; hours += 6) {
    const sample = wall + hours * 3600000;
    offsets.add(wallMilliseconds(formatShowWallTime(new Date(sample).toISOString(), timezone)) - sample);
  }
  return [...new Set([...offsets].map(offset => new Date(wall - offset).toISOString())
    .filter(candidate => formatShowWallTime(candidate, timezone) === canonical))].sort();
}

export function resolveShowWallTime(
  value: string, timezone: string, occurrence?: 'earlier' | 'later',
): string {
  const candidates = showWallTimeCandidates(value, timezone);
  if (!candidates.length) throw new Error('This local time does not exist because the clocks move forward. Choose another time.');
  if (candidates.length > 1 && !occurrence) {
    throw new Error('This local time occurs twice when the clocks move back. Choose the earlier or later occurrence.');
  }
  return occurrence === 'later' ? candidates[candidates.length - 1] : candidates[0];
}

/** Copy wall-clock timing, retaining prep/return calendar-day offsets across DST. */
export function copyShowPeriodTiming(
  source: ShowPeriod, targetShowDate: string, timezone: string,
): ShowPeriodTiming {
  const difference = wallMilliseconds(`${targetShowDate}T12:00`) - wallMilliseconds(`${source.showDate}T12:00`);
  if (difference % DAY !== 0) throw new Error('Choose a valid target show date.');
  const result = {} as ShowPeriodTiming;
  const keys: (keyof ShowPeriodTiming)[] = [
    'prepAt', 'shipAt', 'setupAt', 'showStartAt', 'showEndAt', 'teardownAt', 'returnAt',
  ];
  for (const key of keys) {
    const wall = wallMilliseconds(formatShowWallTime(source[key], timezone)) + difference;
    result[key] = resolveShowWallTime(new Date(wall).toISOString().slice(0, 19), timezone);
  }
  return result;
}
